import type _jquery from "jquery";
import { computed, provide, reactive } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";

import { useFormSettings } from "./use-form-settings.js";
import { useGatingLevel } from "./use-gating-level.js";
import { UserMarketoInputName, type MarketoVals } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import { useFormLogger } from "./use-form-logger.js";

type UseFormUserInputValuesInjectionValues<TEnabledFields extends string = string, TInputValues extends Record<TEnabledFields, any> = Record<TEnabledFields, any>> = InjectionValuesRecord<{
  userInputValues: TInputValues,
  setFormUserInputValuesFromSettings(): void
}>;

type UseFormUserInputValuesInjectionKeys<
  TEnabledFields extends string = string,
  TInputValues extends Record<TEnabledFields, any> = Record<TEnabledFields, any>
> = InjectionKeysRecord<UseFormUserInputValuesInjectionValues<TEnabledFields, TInputValues>>;

const UseFormUserInputValuesInjectionKeys = CreateInjectionKeysRecord<UseFormUserInputValuesInjectionKeys>({
  userInputValues: "userInputValues",
  setFormUserInputValuesFromSettings: "setFormUserInputValuesFromSettings",
});


export function initFormUserInputValues(){
  if (checkValuesAlreadyProvided(UseFormUserInputValuesInjectionKeys)) {
    return;
  }

  const { FormSettings } = useFormSettings({ callerLabel: "setFormUserInputValuesFromSettings" });
  const { CurrentMarketoFormGatingState } = useGatingLevel({ callerLabel: "setFormUserInputValuesFromSettings" });

  const enabledFields = computed(() => {
    const enabledFieldsObj = FormSettings.enabledFields;

    const curGatingState = CurrentMarketoFormGatingState.value;
    if (curGatingState in enabledFieldsObj) {
      return enabledFieldsObj[curGatingState as keyof (typeof enabledFieldsObj)];
    }
    else {
      throw new Error(
        `${curGatingState} does not exist for formExperience '${FormSettings.formSettingsKey}'`
      );
    }
  });

  const userInputValues = reactive({});

  const enabledUserFields = computed(() => {
    return enabledFields.value
      .filter(f => (UserMarketoInputName as readonly string[])
      .includes(f)) as Extract<(typeof enabledFields.value)[number], UserMarketoInputName>[];
  });

  function setFormUserInputValuesFromSettings() {
    type AnyEnabledUserField = (typeof enabledUserFields.value)[number];
    type UserMarketoValues = { [Key in AnyEnabledUserField]: Required<MarketoVals>[Key] };

    for (const key of Object.keys(userInputValues)) {
      delete userInputValues[key];
    }

    for (const prop of enabledUserFields.value) {
      userInputValues[prop] = null as UserMarketoValues[typeof prop];
    }
  }

  provide(UseFormUserInputValuesInjectionKeys.userInputValues, userInputValues);
  provide(UseFormUserInputValuesInjectionKeys.setFormUserInputValuesFromSettings, setFormUserInputValuesFromSettings);
}

export function useFormUserInputValues<
  TEnabledFields extends string, TInputValues extends Record<TEnabledFields, unknown>
>({ callerLabel }: { callerLabel?: string | undefined } = {}) {
    const { logger } = useFormLogger();
  try {
    callerLabel ??= "useFormUserInputValues";
    const userInputValues = injectRequiredWithSelf(
      UseFormUserInputValuesInjectionKeys.userInputValues as UseFormUserInputValuesInjectionKeys<TEnabledFields, TInputValues>["userInputValues"]
    );
    const setFormUserInputValuesFromSettings = injectRequiredWithSelf(UseFormUserInputValuesInjectionKeys.setFormUserInputValuesFromSettings);
    return { userInputValues, setFormUserInputValuesFromSettings };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}